import type { ReactElement } from 'react';
import type { TableBlock, TableRowSpec } from '@shared/types';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableText } from '../../components/Editable';
import { Frame } from './primitives';

/**
 * data_table (docs/data-table-card.md): a table built from a file the rep
 * dropped into the AgentBar — a rate card, a Nielsen/TAPSCAN schedule, a
 * performance export.
 *
 * Every other v3 slide renders values the plan derives. This one renders
 * values a rep supplied, so it follows two rules the others do not need:
 *
 *  1. Cells are printed EXACTLY as `content.table` stores them. No currency
 *     helper, no rounding, no `money()`. The block holds strings for this
 *     reason — a source's own "$11.11" average unit rate need not reproduce
 *     its own "$69,316.00" total, and the slide must show what the file says.
 *  2. A table read off a PDF by the model is unverified until a rep confirms
 *     it. The check happens in chat; the slide itself carries no badge.
 *
 * Column count and rows-per-page are decided upstream by src/domain/table-fit.ts;
 * this component lays out whatever survived and states what did not.
 */

function rowClassName(kind: TableRowSpec['kind']): string {
  if (kind === 'total') return 'mv3-table-row mv3-table-total';
  if (kind === 'subhead') return 'mv3-table-row mv3-dt-subhead';
  return 'mv3-table-row';
}

function omissionNote(table: TableBlock): string {
  const parts: string[] = [];
  const columns = table.omitted?.columns ?? [];
  if (columns.length > 0) {
    parts.push(`${columns.length} more column${columns.length === 1 ? '' : 's'} in the source: ${columns.join(', ')}.`);
  }
  if (table.omitted?.rows) {
    parts.push(`${table.omitted.rows} further row${table.omitted.rows === 1 ? '' : 's'} not shown.`);
  }
  return parts.join(' ');
}

/** Mirrors isEntityTable in src/domain/table-fit.ts (not imported: web keeps domain imports type-only). */
function isEntityTable(table: TableBlock): boolean {
  return table.rows.some((row) => row.detail || row.logoUrl);
}

function SummaryBand({ table }: { table: TableBlock }): ReactElement | null {
  const items = table.summary ?? [];
  if (items.length === 0) return null;
  return (
    <div className="mv3-dt-summary" style={{ gridTemplateColumns: `repeat(${items.length}, 1fr)` }}>
      {items.map((item, index) => (
        <div className="mv3-dt-summary-item" key={index}>
          <span className="mv3-dt-summary-label">{item.label}</span>
          <span className="mv3-dt-summary-value">{item.value}</span>
          {item.unit ? <span className="mv3-dt-summary-unit">{item.unit}</span> : null}
        </div>
      ))}
    </div>
  );
}

/**
 * The first cell of an entity row: logo box, name, detail line. Every row of
 * an entity table keeps the logo box, logo or not, so names line up.
 */
function EntityLabel({ row, value }: { row: TableRowSpec; value: string }): ReactElement {
  return (
    <span className="mv3-dt-entity">
      <span className="mv3-dt-entity-logo">
        {row.logoUrl ? <img src={row.logoUrl} alt={value} /> : null}
      </span>
      <span className="mv3-dt-entity-text">
        <span className="mv3-dt-entity-name">{value}</span>
        {row.detail ? <span className="mv3-dt-entity-detail">{row.detail}</span> : null}
      </span>
    </span>
  );
}

function TableGrid({ table }: { table: TableBlock }): ReactElement {
  const entity = isEntityTable(table);
  // ENTITY_LABEL_WIDTH in table-fit.ts budgets 724px for the label column;
  // spare width goes to it too, so a typical detail line stays on one line.
  const gridTemplateColumns = table.columns
    .map((column, index) => (entity && index === 0 ? 'minmax(724px, 5fr)' : column.emphasis ? '1.3fr' : '1fr'))
    .join(' ');

  return (
    <div className="mv3-table mv3-dt-table">
      <div className="mv3-table-row mv3-table-head" style={{ gridTemplateColumns }}>
        {table.columns.map((column) => (
          <span key={column.key} className={column.align === 'right' ? 'mv3-table-cell-right' : undefined}>
            {column.label}
          </span>
        ))}
      </div>
      {table.rows.map((row, index) => {
        if (row.kind === 'subhead') {
          // A group label spans the table rather than sitting in column one,
          // so it reads as a heading and never sets a column's width.
          const label = table.columns.map((column) => row.cells[column.key]).find(Boolean) ?? '';
          return (
            <div className={rowClassName(row.kind)} key={index}>
              <span>{label}</span>
            </div>
          );
        }
        return (
          <div
            className={entity ? `${rowClassName(row.kind)} mv3-dt-entity-row` : rowClassName(row.kind)}
            key={index}
            style={{ gridTemplateColumns }}
          >
            {table.columns.map((column, columnIndex) =>
              entity && columnIndex === 0 ? (
                <EntityLabel key={column.key} row={row} value={row.cells[column.key] ?? ''} />
              ) : (
              <span
                key={column.key}
                className={
                  [
                    column.align === 'right' ? 'mv3-table-cell-right' : '',
                    column.emphasis ? 'mv3-dt-emphasis' : '',
                  ]
                    .filter(Boolean)
                    .join(' ') || undefined
                }
              >
                {row.cells[column.key] ?? ''}
              </span>
              ),
            )}
          </div>
        );
      })}
    </div>
  );
}

export function DataTablePage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const table = section.content.table ?? null;
  const headline = section.content.headline || section.title;
  const part = table?.part;
  const eyebrow = part ? `${part.index} OF ${part.total}` : null;
  const note = table ? omissionNote(table) : '';

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-dt-root"
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-dt-body">
          <div className="mv3-dt-head">
            <div className="mv3-dt-heading">
              {eyebrow ? <span className="mv3-eyebrow">{eyebrow}</span> : null}
              <h1 className="mv3-h1">
                <EditableText
                  value={headline}
                  field="headline"
                  placeholder="Name this table"
                  readOnly={readOnly}
                  onSave={(value) => actions.saveContent(section, { headline: value })}
                />
              </h1>
            </div>
          </div>

          {table && table.columns.length > 0 ? (
            table.summary && table.summary.length > 0 ? (
              // Band and table centre as one block, so the band sits on the table.
              <div className="mv3-dt-stack">
                <SummaryBand table={table} />
                <TableGrid table={table} />
              </div>
            ) : (
              <TableGrid table={table} />
            )
          ) : (
            <p className="note">This page appears once a file has been read into a table.</p>
          )}

          <div className="mv3-dt-foot">
            <EditableText
              value={table?.caption ?? ''}
              field="body"
              placeholder="Add a source note (optional)"
              readOnly={readOnly}
              onSave={(caption) =>
                actions.saveContent(section, table ? { table: { ...table, caption } } : {})
              }
            />
            {note ? <p className="mv3-dt-omitted">{note}</p> : null}
          </div>
        </div>
      </Frame>
    </PageShell>
  );
}
